import { SkeletonBar, Text } from '@janeapp/burrito-design-system';
import { ErrorView } from '~/components';
import { useAppDispatch, useAppSelector } from '~/state';
import {
  selectSelectedClinic,
  clearSelectedClinic,
  setSelectedClinic,
} from '~/state/activeSelection';
import { PractitionerItem, NoItemsFound } from './components';
import {
  searchPractitionersByLocation,
  selectHasSearched,
  selectSearchHasMore,
  selectSearchLoading,
} from '~/state/search';
import { Button } from 'react-aria-components';
import { selectLocation } from '~/state/location';
import { selectDiscipline } from '~/state/discipline';
import { SearchResultGroupByLocation } from '~/utilities';
import { useRef, useEffect } from 'react';

interface SearchResultListProps {
  results?: SearchResultGroupByLocation[];
}

export const SearchResultList = ({ results }: SearchResultListProps) => {
  const dispatch = useAppDispatch();
  const hasSearched = useAppSelector(selectHasSearched);
  const loading = useAppSelector(selectSearchLoading);
  const location = useAppSelector(selectLocation);
  const discipline = useAppSelector(selectDiscipline);
  const selectedClinic = useAppSelector(selectSelectedClinic);
  const hasMore = useAppSelector(selectSearchHasMore);

  const scrollContainerRef = useRef<HTMLUListElement>(null);
  const savedScrollPosition = useRef<number>(0);
  const isLoadingMore = useRef<boolean>(false);

  const handleClearClinic = () => {
    dispatch(clearSelectedClinic());
  };

  useEffect(() => {
    if (
      isLoadingMore.current &&
      loading === 'succeeded' &&
      scrollContainerRef.current
    ) {
      scrollContainerRef.current.scrollTop = savedScrollPosition.current;
      isLoadingMore.current = false;
    }
  }, [loading, results]);

  const handleLoadMore = () => {
    if (scrollContainerRef.current) {
      savedScrollPosition.current = scrollContainerRef.current.scrollTop;
      isLoadingMore.current = true;
    }
    dispatch(searchPractitionersByLocation({ loadMore: true }));
  };

  if (!hasSearched)
    return (
      <div
        data-testid="mobile-cta"
        className="flex h-full w-full flex-col items-center justify-center"
      >
        <Text className="text-lg font-semibold">
          Find practitioners near you.
        </Text>
        <Text className="text-md text-neutral-80">
          Pick a discipline and get going.
        </Text>
      </div>
    );

  if (loading === 'pending')
    return (
      <div
        data-testid="skeleton-loader"
        className="flex h-full w-full flex-shrink-0 flex-grow-0 flex-col overflow-hidden "
      >
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="border-box flex flex-row gap-3 border-b border-neutral-30 p-4"
          >
            <SkeletonBar size="lg" className="h-[56px] w-[56px] rounded-full" />
            <div className="flex flex-1 flex-col justify-center gap-1">
              <SkeletonBar size="md" className="w-4/5 md:w-3/5" />
              <SkeletonBar size="md" className="w-100 md:w-4/5" />
              <SkeletonBar size="md" className="w-2/5 rounded-full md:w-1/5" />
            </div>
          </div>
        ))}
      </div>
    );

  if (loading === 'failed')
    return (
      <ErrorView
        userMessage="Something went wrong with your search. Try again in a moment."
        message={`Searching for practitioners failed. location: ${JSON.stringify(location)} discipline: ${discipline}`}
        onRetry={() => dispatch(searchPractitionersByLocation())}
      />
    );

  if (loading === 'succeeded' && !results?.length) return <NoItemsFound />;

  if (selectedClinic) {
    return (
      <>
        <div className="flex flex-row items-center justify-between bg-brand-10 px-4 py-3">
          <Text elementType="span" color="default">
            Viewing practitioners at this clinic
          </Text>
          <Button
            data-testid="clear-clinic-button"
            className="focus-ring p-1 text-md text-discovery-90 underline"
            onPress={() => handleClearClinic()}
          >
            Clear
          </Button>
        </div>
        <ul role="menu" className="flex h-full flex-col overflow-y-auto">
          {results
            ?.find(
              (group) =>
                group.clinicLocationGuid === selectedClinic?.clinicLocationGuid
            )
            ?.practitioners?.map((result) => (
              <PractitionerItem
                key={result.marketplacePractitionerLocationId}
                result={result}
              />
            ))}
        </ul>
      </>
    );
  }

  const rollupPractitionerItems = (result: SearchResultGroupByLocation) => {
    const isMoreThan3 = result.practitioners.length > 3;
    const practitionerItems = result.practitioners
      .slice(0, 3)
      .map((practitioner) => (
        <PractitionerItem
          key={practitioner.marketplacePractitionerLocationId}
          result={practitioner}
        />
      ));
    const viewAllButton = isMoreThan3 ? (
      <div
        data-testid="view-all-practitioners"
        key={`${result.clinicLocationGuid}-view-all`}
        className="flex flex-row justify-between gap-5 border-b border-neutral-30 px-5 py-3"
      >
        <Text className="flex-1">
          {`+${result.practitioners.length - 3} ${
            result.practitioners.length - 3 === 1
              ? 'practitioner'
              : 'practitioners'
          } at ${result.clinicName}`}
        </Text>
        <Button
          data-testid="view-all-practitioners-button"
          className="shrink-0 whitespace-nowrap text-md text-brand-80 underline"
          onPress={() => dispatch(setSelectedClinic(result))}
        >
          View all
        </Button>
      </div>
    ) : null;

    return [...practitionerItems, viewAllButton];
  };

  return (
    <ul
      role="menu"
      className="flex h-full flex-col overflow-y-auto"
      ref={scrollContainerRef}
    >
      {results?.map((result) => {
        return rollupPractitionerItems(result);
      })}
      {hasMore && (
        <li className="flex justify-center border-t border-neutral-30 py-4">
          <Button
            onPress={handleLoadMore}
            className="text-md font-semibold text-brand-80 underline focus:outline-none focus:ring-2 focus:ring-brand-60"
          >
            Show more results...
          </Button>
        </li>
      )}
    </ul>
  );
};
